import classNames from "classnames";
import {ReactNode} from "react";

import {CheckMarkIcon} from "@/components/checkMarkIcon/CheckMarkIcon.tsx";
import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {SelectOption} from "@/components/select/Select.types.ts";
import {useFilteredRowSelection} from "@/components/table/hooks/useFilteredRowSelection.ts";
import {LimitAndOffset} from "@/components/table/subComponents/Paginator/Paginator.tsx";
import {Table} from "@/components/table/Table.tsx";
import {ColumnSpec, Sorting, SortingDirection} from "@/components/table/Table.types.ts";
import {Tooltip} from "@/components/toolTip/Tooltip.tsx";
import {ExternalUser} from "@/lib/types/statuspages.ts";
import {ExternalUserRowActions} from "@/pages/statuspages/tabs/external-users/actions/ExternalUserRowActions.tsx";
import {DeleteExternalUserButton} from "@/pages/statuspages/tabs/external-users/components/DeleteExternalUserButton.tsx";

import styles from "./StatusPageExternalUsersTable.module.scss";

interface StatusPageExternalUsersTableProps {
  items: ExternalUser[];
  statusPageID: number;
  sortColumn: string;
  sortDirection: SortingDirection;
  onSortingChange?: (sorting: Sorting) => void;
  noDataComponent: ReactNode;
  pagination?: LimitAndOffset;
  totalRows?: number;
  updatePagination?: (laf: LimitAndOffset) => void;
  paginationOptions?: SelectOption<number>[];
  paginationData?: unknown[];
}

export const StatusPageExternalUsersTable = ({
  items,
  statusPageID,
  sortColumn,
  sortDirection,
  onSortingChange,
  noDataComponent,
  pagination,
  totalRows,
  updatePagination,
  paginationOptions,
  paginationData,
}: StatusPageExternalUsersTableProps) => {
  const {user} = useCurrentUserContext();
  const {rowSelection, setRowSelection} = useFilteredRowSelection({
    data: items,
    getRowId: (row) => row.id.toString(),
  });

  const specs: ColumnSpec<ExternalUser>[] = [
    {
      fieldName: "full_name",
      accessor: (externalUser) => (
        <Tooltip tooltipContent={`${externalUser.first_name} ${externalUser.last_name}`} delayMs={500}>
          <div className={classNames("ellipsis-overflow")}>
            {externalUser.first_name} {externalUser.last_name}
          </div>
        </Tooltip>
      ),
      enableSorting: true,
      fieldTitle: "Name",
      meta: {
        headerClassName: styles.nameColumn,
      },
    },
    {
      fieldName: "is_active",
      accessor: (externalUser) => <CheckMarkIcon enabled={externalUser.is_active} />,
      enableSorting: true,
      fieldTitle: "Active",
      meta: {
        headerClassName: styles.activeColumn,
      },
    },
    {
      fieldName: "external_user__email",
      accessor: (externalUser) => (
        <Tooltip tooltipContent={externalUser.email} delayMs={500}>
          <div className={classNames("ellipsis-overflow")}>{externalUser.email}</div>
        </Tooltip>
      ),
      fieldTitle: "Email",
      meta: {
        headerClassName: styles.emailColumn,
      },
    },
  ];
  if (user?.has_write_access) {
    specs.push({
      fieldName: "quick_actions",
      accessor: (externalUser) => (
        <ExternalUserRowActions statusPageID={statusPageID} user={externalUser} onSuccess={() => setRowSelection({})} />
      ),
      meta: {
        headerClassName: styles.actionColumn,
      },
    });
  }
  const selectedUserIds = Object.keys(rowSelection)
    .map((id) => items[parseInt(id)]?.id)
    .filter((id) => id);
  return (
    <Table
      fixedWidth
      minWidth={"600px"}
      actionBar={
        <div className="d-flex flex-row align-items-center">
          <DeleteExternalUserButton
            userIds={selectedUserIds}
            statusPageID={statusPageID}
            onSuccess={() => setRowSelection({})}
          ></DeleteExternalUserButton>
        </div>
      }
      rows={items}
      getRowId={(row) => row.id.toString()}
      className="rounded-bottom selectable-rows"
      rowSelection={rowSelection}
      setRowSelection={setRowSelection}
      sorting={{column: sortColumn, direction: sortDirection}}
      onSortingChange={onSortingChange}
      noDataComponent={noDataComponent}
      specs={specs}
      initialState={{columnPinning: {right: ["quick_actions"]}}}
      pagination={pagination}
      totalRows={totalRows}
      updatePagination={updatePagination}
      paginationOptions={paginationOptions}
      paginationData={paginationData}
    />
  );
};
